import { useState, useEffect, useRef } from "react";
import { EditorView } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { markdown } from "@codemirror/lang-markdown";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (input: { title: string; body: string }) => Promise<void>;
}

export function NewNoteModal({ open, onClose, onCreate }: Props) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  // Reset both title and body whenever the modal opens. Without this, a
  // cancelled draft's text persists in React state across opens (the
  // CodeMirror view is destroyed and re-created so the editor *looks*
  // empty, but Save would still submit the stale body).
  useEffect(() => {
    if (open) {
      setTitle("");
      setBody("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !editorRef.current || viewRef.current) return;
    const view = new EditorView({
      state: EditorState.create({
        doc: "",
        extensions: [
          markdown(),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) setBody(u.state.doc.toString());
          }),
        ],
      }),
      parent: editorRef.current,
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  async function handleCreate() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onCreate({ title: title.trim(), body });
      setTitle("");
      setBody("");
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sr-modal-root" role="dialog" aria-modal="true">
      <div className="sr-modal-backdrop" onClick={onClose} />
      <div className="sr-modal">
        <header className="sr-modal__header">
          <h2>New memory note</h2>
        </header>
        <div className="sr-modal__body">
          <label className="sr-modal__label">
            Title
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          </label>
          <label className="sr-modal__label">Body (markdown, [[wikilinks]] supported)</label>
          <div className="sr-modal__editor" ref={editorRef} />
        </div>
        <footer className="sr-modal__footer">
          <button type="button" className="sr-btn sr-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="sr-btn"
            onClick={handleCreate}
            disabled={!title.trim() || saving}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
